import { useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { dispatch, getState } from "../../../../state/store";
import { setFwuEvents } from "../../../../state/ducks";
import { selectRum } from "../../../../state/selectors";

import { chartNames, strCode } from "../../../../consts";
import { t } from "../../../../services";
import {
  arrayUtil,
  dashboardUtil,
  deviceUtil,
  firmwareDashboardUtil,
  rumEventsUtil
} from "../../../../utils";
import { useSelectedAppTypes } from "../../../../hooks";

import {
  DashboardBarChart,
  DashboardLineChart,
  FlexContainers,
  GradientLoader,
  WrapperContainer
} from "../../../../components";
import { DashboardTemplate } from "../../../../templates";
import { GridItem, MetricMainValue } from "../styled";

import DeviceTabsSelector from "../components/DeviceTabsSelector";
import FWUStatisticsChart from "../defaultCharts/FWUStatisticsChart";

const FirmwareSection = ({
  getChartFilter = () => {},
  getIsLoading = () => {},
  getIsError = () => {},
  downloadCSV = () => {}
}) => {
  const market = useSelector((store) => store.dashboardFilters.market);
  const fwu = useSelector((store) => store.rum.fwu);
  const reconnectionTimeDuringFWU = useSelector((store) => store.rum.reconnectionTimeDuringFWU);
  const fwuByMarket = useSelector((store) => store.rum.fwuByMarket);
  const device = useSelector((store) => store.dashboardFilters.device);

  const selectedAppTypes = useSelectedAppTypes();

  const appsWithFWUCount = selectedAppTypes?.filter((app) =>
    app.availableDashboards.some((dashboard) => dashboard.name === chartNames.FWU_COUNT)
  );

  const onDownloadCSV = () => {
    const title =
      appsWithFWUCount?.length > 1
        ? strCode.FWU_COUNT_PER_APPLICATION_TITLE
        : market.length > 1
        ? strCode.FWU_COUNT_TITLE
        : strCode.FWU_COUNT_PER_LOCATION_TITLE;

    const res = firmwareDashboardUtil.getFWCountReport(fwu?.data);
    downloadCSV(res, t(title));
  };

  const updatePerformanceAvgSpeed = useMemo(() => {
    const performanceSpeed = fwu?.performance?.map((p) => p.metrics.speed).filter((speed) => speed);

    return performanceSpeed?.length ? arrayUtil.getAverageValue(performanceSpeed) : 0;
  }, [fwu]);

  return (
    <>
      <GridItem gridColumn={"1 / 7"}>
        <WrapperContainer Container={FlexContainers.FlexRowBetween} gap={16}>
          {dashboardUtil.isChartAvailable(selectedAppTypes, chartNames.FWU_STATISTICS) && (
            <FWUStatisticsChart
              fwu={fwu}
              getChartFilter={getChartFilter}
              getIsLoading={getIsLoading}
              getIsError={getIsError}
              downloadCSV={downloadCSV}
            />
          )}

          {dashboardUtil.isChartAvailable(selectedAppTypes, chartNames.FWU_PERFORMANCE) && (
            <DashboardTemplate
              id={chartNames.FWU_PERFORMANCE}
              title={strCode.FWU_PERFORMANCE_TITLE}
              isLoading={getIsLoading(chartNames.FWU_PERFORMANCE)}
              Component={DeviceTabsSelector}
              onUpdateChartData={(devices) => {
                const rum = selectRum(getState());
                const data = rum?.fwu?.data;

                if (data) {
                  const fwuPerformace = firmwareDashboardUtil.getFwUpdatePerformance(data, devices);

                  dispatch(
                    setFwuEvents({
                      performance: fwuPerformace
                    })
                  );
                }
              }}>
              {device?.id !== 0 && (
                <MetricMainValue>
                  {getIsLoading(chartNames.FWU_PERFORMANCE) ? (
                    <GradientLoader width={120} height={32} />
                  ) : (
                    `${updatePerformanceAvgSpeed}s`
                  )}
                </MetricMainValue>
              )}

              <DashboardLineChart
                data={fwu?.performance}
                isLoading={getIsLoading(chartNames.FWU_PERFORMANCE)}
                isError={getIsError(chartNames.FWU_PERFORMANCE)}
                yAxisLabel={strCode.MAX_COUNT_LABEL}
                metricsConfig={rumEventsUtil.getMetricsConfig({
                  chartName: chartNames.FWU_PERFORMANCE,
                  deviceType: getChartFilter(chartNames.FWU_PERFORMANCE),
                  coloredMetric: "fwUpdates"
                })}
                withoutChartAvgValue={device?.id === 0}
              />
            </DashboardTemplate>
          )}
        </WrapperContainer>
      </GridItem>

      {dashboardUtil.isChartAvailable(selectedAppTypes, chartNames.FWU_COUNT) && (
        <GridItem gridColumn={"1 / 7"}>
          <DashboardTemplate
            id={chartNames.FWU_COUNT}
            title={
              appsWithFWUCount?.length > 1
                ? strCode.FWU_COUNT_PER_APPLICATION_TITLE
                : market.length > 1
                ? strCode.FWU_COUNT_TITLE
                : strCode.FWU_COUNT_PER_LOCATION_TITLE
            }
            isLoading={getIsLoading(chartNames.FWU_COUNT)}
            onDownloadCSV={fwu?.data ? onDownloadCSV : null}>
            <DashboardBarChart
              data={fwuByMarket?.statistics}
              isLoading={getIsLoading(chartNames.FWU_COUNT)}
              isError={getIsError(chartNames.FWU_COUNT)}
              yAxisLabel={strCode.COUNT_LABEL}
              metricsConfig={rumEventsUtil.getMetricsConfig({
                chartName: chartNames.FWU_COUNT,
                deviceType: getChartFilter(chartNames.FWU_COUNT)
              })}
              isCustomTicks
            />
          </DashboardTemplate>
        </GridItem>
      )}

      <GridItem gridColumn={"1 / 7"}>
        <WrapperContainer Container={FlexContainers.FlexRowBetween} gap={16}>
          {dashboardUtil.isChartAvailable(selectedAppTypes, chartNames.FWU_ERRORS) && (
            <DashboardTemplate
              id={chartNames.FWU_ERRORS}
              title={strCode.FWU_ERRORS_TITLE}
              isLoading={getIsLoading(chartNames.FWU_ERRORS)}
              deviceSelect
              onUpdateChartData={(devices) => {
                const rum = selectRum(getState());
                const data = rum?.fwu?.data;

                if (data) {
                  const fwuErrors = firmwareDashboardUtil.getFwUpdateErrors(data, devices);

                  dispatch(
                    setFwuEvents({
                      errors: fwuErrors
                    })
                  );
                }
              }}>
              <DashboardBarChart
                isErrorMetrics
                data={fwu?.errors}
                isLoading={getIsLoading(chartNames.FWU_ERRORS)}
                isError={getIsError(chartNames.FWU_ERRORS)}
                yAxisLabel={strCode.COUNT_LABEL}
                metricsConfig={rumEventsUtil.getMetricsConfig({
                  chartName: chartNames.FWU_ERRORS,
                  deviceType: getChartFilter(chartNames.FWU_ERRORS)
                })}
              />
            </DashboardTemplate>
          )}

          {dashboardUtil.isChartAvailable(
            selectedAppTypes,
            chartNames.RECONNECTION_TIME_DURING_FWU
          ) && (
            <DashboardTemplate
              id={chartNames.RECONNECTION_TIME_DURING_FWU}
              title={strCode.RECONNECTION_TIME_DURING_FWU_TITLE}
              isLoading={getIsLoading(chartNames.RECONNECTION_TIME_DURING_FWU)}
              mt={24}>
              <DashboardLineChart
                id={chartNames.RECONNECTION_TIME_DURING_FWU}
                data={reconnectionTimeDuringFWU}
                isLoading={getIsLoading(chartNames.RECONNECTION_TIME_DURING_FWU)}
                isError={getIsError(chartNames.RECONNECTION_TIME_DURING_FWU)}
                yAxisLabel={strCode.MAX_AVG_SPEED_LABEL}
                yAxisUnit="s"
                metricsConfig={rumEventsUtil.getMetricsConfig({
                  chartName: chartNames.RECONNECTION_TIME_DURING_FWU,
                  deviceType: deviceUtil.getDeviceTypesByName(
                    getChartFilter(chartNames.RECONNECTION_TIME_DURING_FWU)
                      ? getChartFilter(chartNames.RECONNECTION_TIME_DURING_FWU)[0]
                      : null
                  ),
                  coloredMetrics: {
                    mono: "mono",
                    charger: "charger",
                    holder: "holder"
                  }
                })}
                deleteNullMetrics
              />
            </DashboardTemplate>
          )}
        </WrapperContainer>
      </GridItem>
    </>
  );
};

FirmwareSection.propTypes = {
  getChartFilter: PropTypes.func.isRequired,
  getIsLoading: PropTypes.func.isRequired,
  getIsError: PropTypes.func.isRequired,
  downloadCSV: PropTypes.func.isRequired
};

export default FirmwareSection;
